import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { LanguageSelector } from "../commons/LanguageSelector.js";
import { Page } from "../commons/layout/Page.js";
import { PageContainer } from "../commons/layout/PageContainer.js";
import { useOnboardingFlow } from "../features/organisations/onboarding/useOnboardingFlow.js";
import { CreateNewOrganisationPage } from "./CreateNewOrganisationPage.js";
import { LogoutButton } from "./LogoutButton.js";
import { AvailableUser, SelectUserList } from "./SelectUserList.js";
import { useAuth } from "./useAuth.js";
import { useSelectedUser } from "./useSelectedUser.js";

interface Props {
  availableUsers: AvailableUser[];
  availableUsersIsLoading: boolean;
  onMount: () => Promise<unknown>;
}

export const SelectUserPage = ({
  availableUsersIsLoading,
  availableUsers,
  onMount,
}: Props) => {
  const { belongsToGroup } = useAuth();
  const { t } = useTranslate(["Global"]);
  const { setSelectedUserId } = useSelectedUser();

  const { setOnboardingFlowStage } = useOnboardingFlow();

  const selectUser = React.useCallback(
    (user: AvailableUser) => {
      setSelectedUserId(user.id);
    },
    [setSelectedUserId]
  );

  React.useEffect(() => {
    onMount();
  }, [onMount]);

  const [isCreatingNew, setIsCreatingNew] = React.useState(false);

  if (isCreatingNew) {
    return (
      <CreateNewOrganisationPage
        referral={null}
        onComplete={userId => {
          setOnboardingFlowStage("form");
          setSelectedUserId(userId);
        }}
        onCancel={() => {
          setIsCreatingNew(false);
        }}
      />
    );
  }

  return (
    <Page
      hideNavigation
      isTopBarVisible
      topbarItems={
        <>
          <LogoutButton color={"inherit"} />
          <LanguageSelector />
        </>
      }
    >
      <PageContainer $fullHeight>
        <Stack
          spacing={1}
          justifyContent={"center"}
          alignItems={"center"}
          flex={1}
        >
          <Box>
            <Typography variant="h1" padding={2}>
              {t("Select organisation", { ns: "Global" })}
            </Typography>
            <SelectUserList
              isLoading={availableUsersIsLoading}
              availableUsers={availableUsers}
              selectedUser={null}
              handleSelect={selectUser}
            />
            {belongsToGroup("org-creator") && (
              <Button
                startIcon={<AddIcon />}
                color="secondary"
                onClick={() => {
                  setIsCreatingNew(true);
                }}
              >
                {t("Create new organisation", { ns: "Global" })}
              </Button>
            )}
          </Box>
        </Stack>
      </PageContainer>
    </Page>
  );
};
